import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
// import FullscreenIcon from "@mui/icons-material/Fullscreen";
// import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import IosShareIcon from "@mui/icons-material/IosShare";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CommentIcon from "@mui/icons-material/Comment";
// import Fullscreen from "core/components/Fullscreen";
import { styled } from "@mui/material/styles";
import { shareURL } from "@telegram-apps/sdk";
import { isTelegramMiniApp } from "core/utils/telegram";
import { useArena } from "core/components/ArenaProvider";
import useIsMobile from "arena/hooks/useIsMobile";
// import { canVideoFullscreen } from "core/utils/features";

// Стилизованная кнопка для панели управления
const ControlPanelButton = styled(IconButton)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.1)",
  color: "white",
  backdropFilter: "blur(4px)",
  // border: "1px solid rgba(255, 255, 255, 0.1)",
  width: 48,
  height: 48,
  "&:hover": {
    background: "rgba(0, 0, 0, 0.8)",
    transform: "scale(1.1)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  transition: "all 0.2s ease-in-out",

  // Уменьшаем размер на мобильных
  [theme.breakpoints.down("md")]: {
    width: 40,
    height: 40,
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
  },
}));

export default function ShortyControlPanel({
  content,
  videoState,
  videoActions,
  // videoElement,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
  onOpenComments,
}) {
  const arena = useArena();
  const isMobile = useIsMobile();
  // Проверка доступности функции поделиться
  const isShareAvailable = () => {
    if (!process.browser) return false;
    if (!arena?.isShareEnabled) return false; // Проверка настроек арены
    if (content?.hideShare) return false;

    if (process.env.NODE_ENV === "production") {
      if (isTelegramMiniApp) {
        return shareURL.isAvailable();
      } else {
        return "share" in navigator;
      }
    }
    return true; // В dev режиме всегда показываем
  };

  // Проверка доступности комментариев
  const isCommentsAvailable = () => {
    if (!arena?.isCommentsEnabled) return false;
    if (!content?.isCommentsEnabled) return false;
    return true;
  };

  const handleShare = () => {
    if (!content) return;

    const shareData = {
      title: content.title,
      text: content.description || content.title,
      url: window.location.href,
    };

    if (isTelegramMiniApp) {
      shareURL(shareData.url, shareData.title);
    } else if ("share" in navigator) {
      navigator.share(shareData);
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        right: isMobile ? 4 : -70, // На мобильных внутри, на десктопе снаружи
        bottom: 10,
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 0.8 : 1.5,
        transition: "all 0.3s ease-in-out",
        // Темный прозрачный фон на мобильных
        ...(isMobile
          ? {
              borderRadius: 2,
              padding: 1,
              bottom: 36,
            }
          : {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(1px)",
              borderRadius: 2,
              padding: 1,
              bottom: 20,
            }),
      }}
    >
      {/* Кнопка поделиться */}
      {isShareAvailable() && (
        <ControlPanelButton onClick={handleShare} size="large">
          <IosShareIcon />
        </ControlPanelButton>
      )}

      {/* Кнопка комментариев */}
      {isCommentsAvailable() && (
        <ControlPanelButton onClick={onOpenComments} size="large">
          <CommentIcon />
        </ControlPanelButton>
      )}

      {/* Навигация - предыдущее видео */}
      <ControlPanelButton
        onClick={onPrevious}
        disabled={!canGoPrevious}
        size="large"
        sx={{
          opacity: canGoPrevious ? 1 : 0.4,
        }}
      >
        <ArrowUpwardIcon />
      </ControlPanelButton>

      {/* Навигация - следующее видео */}
      <ControlPanelButton
        onClick={onNext}
        disabled={!canGoNext}
        size="large"
        sx={{
          opacity: canGoNext ? 1 : 0.4,
        }}
      >
        <ArrowDownwardIcon />
      </ControlPanelButton>

      {/* Полноэкранный режим
      {(() => {
        // Логика для iOS нативного фулскрина (как в FullscreenButton)
        if (videoElement) {
          const vid = videoElement.ref?.current;

          if (vid && vid.webkitSupportsFullscreen && canVideoFullscreen) {
            return (
              <ControlPanelButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  vid.webkitEnterFullscreen();
                }}
                size="large"
              >
                <FullscreenIcon />
              </ControlPanelButton>
            );
          }
        }

        // Обычная логика для всех остальных устройств
        return (
          <Fullscreen target="player">
            {({ isFullscreen, enabled, toggleFullscreen }) => {
              if (!enabled) return null;

              const Icon = isFullscreen ? FullscreenExitIcon : FullscreenIcon;

              return (
                <ControlPanelButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleFullscreen();
                  }}
                  size="large"
                >
                  <Icon />
                </ControlPanelButton>
              );
            }}
          </Fullscreen>
        );
      })()} */}

      {/* Кнопка громкости */}
      <ControlPanelButton
        onClick={() => videoActions.toggleMute()}
        size="large"
      >
        {videoState.muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </ControlPanelButton>
    </Box>
  );
}

ShortyControlPanel.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    hideShare: PropTypes.bool,
    isCommentsEnabled: PropTypes.bool,
  }),
  videoState: PropTypes.shape({
    muted: PropTypes.bool,
  }),
  videoActions: PropTypes.shape({
    toggleMute: PropTypes.func,
  }),
  videoElement: PropTypes.shape({
    ref: PropTypes.shape({
      current: PropTypes.shape({
        webkitEnterFullscreen: PropTypes.func,
        webkitSupportsFullscreen: PropTypes.bool,
      }),
    }),
  }),
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
  canGoPrevious: PropTypes.bool,
  canGoNext: PropTypes.bool,
  onOpenComments: PropTypes.func,
};
